import { Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { useState } from 'react';
import { useAuditLogs, type AuditQuery } from '../api/hooks';
import type { AuditLog } from '../types';
import { dateTime } from '../utils/format';

const ACTION_COLOR: Record<string, string> = {
  Create: 'green',
  Update: 'blue',
  UpdateStatus: 'orange',
  Delete: 'red',
};

const ENTITY_TYPES = ['Investor', 'Investment', 'PrincipalWithdrawal', 'ProfitPayment', 'User'];

export default function AuditLogPage() {
  const [query, setQuery] = useState<AuditQuery>({ page: 1, pageSize: 20 });
  const { data, isFetching } = useAuditLogs(query);

  return (
    <>
      <Typography.Title level={3}>Audit log</Typography.Title>

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear
            placeholder="Search action, entity or user"
            style={{ width: 280 }}
            onSearch={(value) => setQuery((q) => ({ ...q, search: value || undefined, page: 1 }))}
          />
          <Select
            allowClear
            placeholder="Entity type"
            style={{ width: 220 }}
            options={ENTITY_TYPES.map((t) => ({ value: t, label: t }))}
            onChange={(value) => setQuery((q) => ({ ...q, entityType: value, page: 1 }))}
          />
        </Space>

        <Table<AuditLog>
          rowKey="id"
          loading={isFetching}
          dataSource={data?.items ?? []}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: query.page,
            pageSize: query.pageSize,
            total: data?.totalCount ?? 0,
            showSizeChanger: true,
            onChange: (page, pageSize) => setQuery((q) => ({ ...q, page, pageSize })),
          }}
          expandable={{
            expandedRowRender: (row) => (
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Typography.Text strong>Old values</Typography.Text>
                  <pre style={preStyle}>{format(row.oldValues)}</pre>
                </div>
                <div>
                  <Typography.Text strong>New values</Typography.Text>
                  <pre style={preStyle}>{format(row.newValues)}</pre>
                </div>
              </Space>
            ),
          }}
          columns={[
            { title: 'When', dataIndex: 'createdAt', render: dateTime },
            { title: 'User', dataIndex: 'username', render: (v: string | null) => v ?? 'system' },
            {
              title: 'Action',
              dataIndex: 'action',
              render: (value: string) => <Tag color={ACTION_COLOR[value] ?? 'default'}>{value}</Tag>,
            },
            { title: 'Entity', dataIndex: 'entityType', render: (v: string | null) => v ?? '—' },
            { title: 'Entity id', dataIndex: 'entityId', render: (v: number | null) => v ?? '—' },
            { title: 'IP', dataIndex: 'ipAddress', render: (v: string | null) => v ?? '—' },
          ]}
        />
      </Card>
    </>
  );
}

const preStyle: React.CSSProperties = {
  background: '#fafafa',
  padding: 8,
  borderRadius: 4,
  maxHeight: 200,
  overflow: 'auto',
  margin: '4px 0 0',
  fontSize: 12,
};

function format(json: string | null) {
  if (!json) return '—';
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}
